import { RotateCcw } from "lucide-react";
import { useState } from "react";
import {
  DEFAULT_ACCESSIBILITY,
  readAccessibility,
  saveAccessibility,
  type AccessibilityPrefs
} from "@/shell/accessibility";

type Option<T extends string> = { value: T; label: string };

const TEXT_SCALE_OPTIONS: Option<AccessibilityPrefs["textScale"]>[] = [
  { value: "small", label: "Menor" },
  { value: "regular", label: "Padrão" },
  { value: "large", label: "Maior" },
  { value: "xlarge", label: "Muito maior" }
];
const FONT_OPTIONS: Option<AccessibilityPrefs["font"]>[] = [
  { value: "padrao", label: "Padrão" },
  { value: "legivel", label: "Leitura fácil" },
  { value: "sistema", label: "Do sistema" }
];
const LINE_OPTIONS: Option<AccessibilityPrefs["lineSpacing"]>[] = [
  { value: "padrao", label: "Padrão" },
  { value: "amplo", label: "Mais espaçado" }
];
const DENSITY_OPTIONS: Option<AccessibilityPrefs["density"]>[] = [
  { value: "padrao", label: "Padrão" },
  { value: "compacta", label: "Compacta" }
];

function Segment<T extends string>({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="a11y-segment" role="radiogroup" aria-label={label}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={value === option.value ? "is-active" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Preferências → Acessibilidade (2026-10-16), no molde do techia hub: tamanho
 * do texto, fonte, espaço entre linhas, versão compacta e foco reforçado.
 * Vale na hora, só neste navegador (`shell/accessibility.ts`).
 */
export function AccessibilityPage() {
  const [prefs, setPrefs] = useState<AccessibilityPrefs>(readAccessibility);
  const update = (changes: Partial<AccessibilityPrefs>) => {
    const next = { ...prefs, ...changes };
    setPrefs(next);
    saveAccessibility(next);
  };
  const isDefault = (Object.keys(DEFAULT_ACCESSIBILITY) as Array<keyof AccessibilityPrefs>).every(
    key => prefs[key] === DEFAULT_ACCESSIBILITY[key]
  );

  return (
    <div className="ajustes-panel account-panel">
      <header className="panel-header">
        <div>
          <h2>Acessibilidade</h2>
          <p>Ajuste o texto e o espaço da plataforma ao seu jeito de ler. Vale na hora, neste navegador.</p>
        </div>
      </header>

      <section className="panel-section">
        <div className="account-section-head">
          <div>
            <h3>Texto e leitura</h3>
            <p className="account-section-hint">Tamanho, fonte e espaço entre linhas em toda a plataforma. A prévia ao lado muda na hora.</p>
          </div>
          <button type="button" className="secondary-button" disabled={isDefault} onClick={() => update(DEFAULT_ACCESSIBILITY)}>
            <RotateCcw size={15} aria-hidden="true" /> Restaurar padrão
          </button>
        </div>
        <div className="a11y-grid">
          <div className="a11y-fields">
            <div className="a11y-field">
              <span className="a11y-field-label">Tamanho do texto</span>
              <Segment label="Tamanho do texto" options={TEXT_SCALE_OPTIONS} value={prefs.textScale} onChange={textScale => update({ textScale })} />
            </div>
            <div className="a11y-field">
              <span className="a11y-field-label">Fonte</span>
              <Segment label="Fonte" options={FONT_OPTIONS} value={prefs.font} onChange={font => update({ font })} />
              <small>A de leitura fácil diferencia melhor letras parecidas, como I, l e 1.</small>
            </div>
            <div className="a11y-field">
              <span className="a11y-field-label">Espaço entre linhas</span>
              <Segment label="Espaço entre linhas" options={LINE_OPTIONS} value={prefs.lineSpacing} onChange={lineSpacing => update({ lineSpacing })} />
              <small>Mais respiro em textos longos, como as matérias e as respostas do Copiloto.</small>
            </div>
          </div>
          <div className="a11y-preview" aria-label="Prévia do texto">
            <span className="a11y-preview-eyebrow">Prévia</span>
            <strong>Ministério anuncia novo programa de saneamento</strong>
            <p>
              A matéria cita a empresa no título e traz a fala do porta-voz sobre o prazo de 12/10.
              Polaridade positiva, com destaque para o investimento de R$ 1,2 bi no item I-01.
            </p>
          </div>
        </div>
      </section>

      <section className="panel-section">
        <h3>Versão compacta</h3>
        <p className="account-section-hint">Menos espaço entre os elementos e controles mais baixos, pra caber mais coisa na tela.</p>
        <Segment label="Densidade" options={DENSITY_OPTIONS} value={prefs.density} onChange={density => update({ density })} />
      </section>

      <section className="panel-section">
        <h3>Foco do teclado</h3>
        <p className="account-section-hint">Ajuda a enxergar onde você está ao navegar com Tab.</p>
        <label className="shortcuts-toggle">
          <input type="checkbox" checked={prefs.strongFocus} onChange={event => update({ strongFocus: event.target.checked })} />
          <span>Destacar sempre o elemento focado com um contorno grosso na cor de destaque</span>
        </label>
      </section>
    </div>
  );
}
